import type { AnvilCommand } from "./types";

/**
 * Scores a command against a normalised (trimmed, lower-cased) query.
 * 3 = label / label-word prefix match, 2 = label substring, 1 = keyword match,
 * 0 = no match (excluded).
 */
const scoreCommand = (command: AnvilCommand, query: string): number => {
  const label = command.label.toLowerCase();
  const words = label.split(/\s+/);

  if (label.startsWith(query) || words.some((word) => word.startsWith(query))) {
    return 3;
  }

  if (label.includes(query)) {
    return 2;
  }

  if ((command.keywords ?? []).some((kw) => kw.toLowerCase().includes(query))) {
    return 1;
  }

  return 0;
};

/**
 * Filters and ranks commands for the Anvil command palette. An empty query
 * returns every command in its original order; otherwise matches are ranked by
 * score with original order preserved for ties (stable).
 */
export function filterCommands(
  commands: AnvilCommand[],
  query: string,
): AnvilCommand[] {
  const normalised = query.trim().toLowerCase();

  if (!normalised) {
    return [...commands];
  }

  return commands
    .map((command, index) => ({
      command,
      index,
      score: scoreCommand(command, normalised),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.command);
}
